import React, { useState } from 'react';
import Card from 'react-bootstrap/Card';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { routerAnimation } from '../../constant/routers';
import { getColorThemeByName, getTheme } from '../../helper';
import { privacySections, termsSections } from './legalContent';

const API = 'https://folder2zip-api.its-ghaith.com';

const legalLinks = [
    { href: '/legal/agb.html', name: 'AGB' },
    { href: '/legal/datenschutz.html', name: 'Datenschutz' },
    { href: '/legal/impressum.html', name: 'Impressum' },
    { href: '/legal/widerruf.html', name: 'Vertrag widerrufen' },
    { href: '/legal/kuendigen.html', name: 'Verträge hier kündigen' },
];

const submitLegal = async (path, data) => {
    const response = await fetch(`${API}${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify(data),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(json.error || `HTTP ${response.status}`);
    return json;
};

const downloadReceipt = (filename, text) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const LegalNavigationComponent = (Props) => (
    <div className='row mt-4 mb-4'>
        <div className='col-12'>
            {legalLinks.map((element, index) => (
                <NavLink
                    key={index}
                    className='text-decoration-none me-3'
                    to={element.href}
                    style={({ isActive }) => ({
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        color: isActive ? Props.colorTheme.activeColor : Props.theme.normalTextColor,
                    })}
                >
                    {element.name}
                </NavLink>
            ))}
        </div>
    </div>
);

const LegalNavigation = connect((state) => ({
    theme: getTheme(state.theme.isDarkTheme),
    colorTheme: getColorThemeByName(state.theme.nameColorTheme),
}))(LegalNavigationComponent);

const LegalPageComponent = (Props) => (
    <>
        <Helmet><title>{Props.browserTitle}</title></Helmet>
        <motion.div {...routerAnimation} className='row mt-4 ms-2 me-2'>
            <div>
                <h1>{Props.title}</h1>
                <div style={{ width: '10%', height: '0%', border: `1px solid ${Props.colorTheme.activeColor}` }}></div>
                <div className='mt-2' style={{ width: '7%', height: '0%', border: `1px solid ${Props.colorTheme.activeColor}` }}></div>
                <LegalNavigation />
                {Props.children}
                <LegalNavigation />
            </div>
        </motion.div>
    </>
);

const LegalPage = connect((state) => ({
    colorTheme: getColorThemeByName(state.theme.nameColorTheme),
}))(LegalPageComponent);

const LegalSections = ({ sections }) => (
    <>
        {sections.map((section, index) => (
            <div key={index}>
                <h2 className='mt-5'>{section.title}</h2>
                {section.paragraphs.map((paragraph, paragraphIndex) => (
                    <p key={paragraphIndex} className='mt-2 text-jus' style={{ textAlign: 'justify' }}>
                        {paragraph}
                    </p>
                ))}
            </div>
        ))}
    </>
);

const Terms = () => (
    <LegalPage browserTitle='AGB – Folder2Zip' title='Allgemeine Geschäfts- und Nutzungsbedingungen für Folder2Zip'>
        <p className='mt-4'>Stand: 4. Oktober 2026</p>
        <LegalSections sections={termsSections} />
    </LegalPage>
);

const Privacy = () => (
    <LegalPage browserTitle='Datenschutz – Folder2Zip' title='Datenschutzerklärung'>
        <p className='mt-4'>Stand: 4. Oktober 2026</p>
        <LegalSections sections={privacySections} />
    </LegalPage>
);

const ImprintComponent = (Props) => {
    const cardStyle = { backgroundColor: Props.theme.lightBackColor };
    const cardTextStyle = { color: Props.isDarkTheme ? 'white' : '#302e4d' };

    return (
        <LegalPage browserTitle='Impressum – Folder2Zip' title='Impressum'>
            <p className='mt-4'>Angaben gemäß § 5 DDG.</p>
            <Card className='mt-5' style={cardStyle}>
                <Card.Body style={cardTextStyle}>
                    <Card.Text style={cardTextStyle}>
                        <strong>Mohammad Ghaith Albaba, Einzelunternehmer</strong><br />
                        Karlstraße 42<br />
                        06844 Dessau-Roßlau<br />
                        Deutschland<br /><br />
                        Telefon: +49 340 87115025<br />
                        E-Mail: <a href='mailto:f2z.business@its-ghaith.com'>f2z.business@its-ghaith.com</a>
                    </Card.Text>
                </Card.Body>
            </Card>
            <h2 className='mt-5'>Verbraucherstreitbeilegung</h2>
            <p className='mt-2 text-jus' style={{ textAlign: 'justify' }}>
                Der Anbieter ist nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
            </p>
            <p className='mt-2 text-jus' style={{ textAlign: 'justify' }}>
                Die frühere EU-Plattform zur Online-Streitbeilegung wird nicht mehr verlinkt, da sie eingestellt wurde.
            </p>
        </LegalPage>
    );
};

const Imprint = connect((state) => ({
    isDarkTheme: state.theme.isDarkTheme,
    theme: getTheme(state.theme.isDarkTheme),
}))(ImprintComponent);

const Receipt = ({ text, filename, buttonText, colorTheme, textStyle }) => text ? (
    <div className='mt-3' style={textStyle}>
        {text.split('\n').map((line, index) => <div key={index}>{line || <br />}</div>)}
        <button
            className='btn mt-3'
            onClick={() => downloadReceipt(filename, text)}
            style={{ background: colorTheme.activeColor, color: 'white' }}
        >
            {buttonText}
        </button>
    </div>
) : null;

const WithdrawComponent = (Props) => {
    const { register, handleSubmit, formState: { errors } } = useForm({ defaultValues: { plan: 'monthly' } });
    const [disabled, setDisabled] = useState(false);
    const [receipt, setReceipt] = useState('');
    const [error, setError] = useState('');
    const cardStyle = { backgroundColor: Props.theme.lightBackColor };
    const cardTextStyle = { color: Props.isDarkTheme ? 'white' : '#302e4d' };

    const onSubmit = async (data) => {
        setDisabled(true);
        setReceipt('');
        setError('');
        try {
            const response = await submitLegal('/api/legal/withdraw', data);
            setReceipt(response.receiptText || `Folder2Zip – Widerruf eingegangen\nEingang: ${response.receivedAt}`);
        } catch (e) {
            setError(`Übermittlung fehlgeschlagen: ${e.message}`);
        } finally {
            setDisabled(false);
        }
    };

    return (
        <LegalPage browserTitle='Widerruf – Folder2Zip' title='Widerrufsbelehrung und elektronische Widerrufsfunktion'>
            <h2 className='mt-5'>Widerrufsrecht</h2>
            <p className='mt-2 text-jus' style={{ textAlign: 'justify' }}>Du hast als Verbraucher das Recht, binnen 14 Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.</p>
            <p className='mt-2 text-jus' style={{ textAlign: 'justify' }}>Die Widerrufsfrist beträgt 14 Tage ab dem Tag des Vertragsschlusses. Um das Widerrufsrecht auszuüben, musst du Mohammad Ghaith Albaba, Einzelunternehmer, Karlstraße 42, 06844 Dessau-Roßlau, Deutschland, Telefon +49 340 87115025, E-Mail f2z.business@its-ghaith.com, mittels einer eindeutigen Erklärung über deinen Entschluss informieren.</p>
            <p className='mt-2 text-jus' style={{ textAlign: 'justify' }}>Du kannst dafür insbesondere die nachfolgende elektronische Widerrufsfunktion verwenden. Zur Wahrung der Frist reicht es aus, dass du die Widerrufserklärung vor Ablauf der Widerrufsfrist absendest.</p>
            <p className='mt-2 text-jus' style={{ textAlign: 'justify' }}>Folder2Zip verlangt derzeit keine ausdrückliche Zustimmung dazu, dass dein Widerrufsrecht wegen einer sofortigen Bereitstellung digitaler Inhalte vor Ablauf der 14 Tage erlischt. Die Aktivierung von Plus allein soll das gesetzliche Widerrufsrecht deshalb nicht vorzeitig beenden.</p>

            <h2 className='mt-5'>Folgen des Widerrufs</h2>
            <p className='mt-2 text-jus' style={{ textAlign: 'justify' }}>Bei einem wirksamen Widerruf werden die im Rahmen der Managed-Payments-Transaktion geschuldeten Rückzahlungen unverzüglich und spätestens binnen 14 Tagen nach Eingang deiner Widerrufserklärung veranlasst. Die Rückzahlung erfolgt grundsätzlich über dasselbe Zahlungsmittel, das bei der ursprünglichen Zahlung verwendet wurde, soweit gesetzlich zulässig und nichts Abweichendes vereinbart wurde.</p>

            <h2 className='mt-5'>Vertrag widerrufen</h2>
            <p className='mt-2'>Diese Funktion ist während der gesetzlichen Widerrufsfrist für online geschlossene Verträge vorgesehen.</p>

            <Card className='mt-4' style={cardStyle}>
                <Card.Body style={cardTextStyle}>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className='row formRow mt-3'>
                            <div className='col-12 col-md-6'>
                                <label className='form-label'>Name</label>
                                <input className='form-control formInput' {...register('name', { required: 'Bitte gib deinen Namen ein.', maxLength: 200 })} />
                                {errors.name && <span className='errorMessage'>{errors.name.message}</span>}
                            </div>
                            <div className='col-12 col-md-6 mt-3 mt-md-0'>
                                <label className='form-label'>E-Mail für die Eingangsbestätigung</label>
                                <input type='email' className='form-control formInput' {...register('email', { required: 'Bitte gib deine E-Mail-Adresse ein.' })} />
                                {errors.email && <span className='errorMessage'>{errors.email.message}</span>}
                            </div>
                        </div>
                        <div className='row formRow mt-3'>
                            <div className='col-12 col-md-8'>
                                <label className='form-label'>Vertrags-/Bestellreferenz</label>
                                <input className='form-control formInput' placeholder='z. B. Stripe-Belegnummer, Subscription-ID oder Kaufdatum' {...register('contractReference', { required: 'Bitte gib eine Vertrags- oder Bestellreferenz ein.', maxLength: 300 })} />
                                {errors.contractReference && <span className='errorMessage'>{errors.contractReference.message}</span>}
                            </div>
                            <div className='col-12 col-md-4 mt-3 mt-md-0'>
                                <label className='form-label'>Tarif</label>
                                <select className='form-control formInput' {...register('plan')}>
                                    <option value='monthly'>Monatlich</option>
                                    <option value='yearly'>12 Monate → monatlich</option>
                                    <option value='unknown'>Unbekannt</option>
                                </select>
                            </div>
                        </div>
                        <button className='btn mt-3' type='submit' disabled={disabled} style={{ background: Props.colorTheme.activeColor, color: 'white' }}>Widerruf bestätigen</button>
                    </form>
                    {error && <p className='mt-3' style={cardTextStyle}>{error}</p>}
                    <Receipt text={receipt} filename='folder2zip-widerruf.txt' buttonText='Eingangsbeleg herunterladen' colorTheme={Props.colorTheme} textStyle={cardTextStyle} />
                </Card.Body>
            </Card>

            <h2 className='mt-5'>Muster-Widerrufsformular</h2>
            <p className='mt-2'>Du musst dieses Muster nicht verwenden. Du kannst deine Widerrufserklärung auch per E-Mail oder über die elektronische Funktion oben abgeben.</p>
            <p className='mt-2'>
                An: Mohammad Ghaith Albaba, Einzelunternehmer, Karlstraße 42, 06844 Dessau-Roßlau, Deutschland, E-Mail: f2z.business@its-ghaith.com<br /><br />
                Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über Folder2Zip Plus.<br />
                Bestellt am: ____________________<br />
                Name: _________________________<br />
                Anschrift: ______________________<br />
                Datum: _________________________<br />
                Unterschrift nur bei Erklärung auf Papier: _________________________
            </p>
        </LegalPage>
    );
};

const Withdraw = connect((state) => ({
    isDarkTheme: state.theme.isDarkTheme,
    theme: getTheme(state.theme.isDarkTheme),
    colorTheme: getColorThemeByName(state.theme.nameColorTheme),
}))(WithdrawComponent);

const CancelComponent = (Props) => {
    const { register, handleSubmit, watch, formState: { errors } } = useForm({
        defaultValues: {
            plan: 'monthly',
            contractReference: 'Folder2Zip Plus',
            cancellationType: 'ordinary',
            desiredEnd: 'earliest',
        },
    });
    const cancellationType = watch('cancellationType');
    const desiredEnd = watch('desiredEnd');
    const [disabled, setDisabled] = useState(false);
    const [receipt, setReceipt] = useState('');
    const [error, setError] = useState('');
    const cardStyle = { backgroundColor: Props.theme.lightBackColor };
    const cardTextStyle = { color: Props.isDarkTheme ? 'white' : '#302e4d' };

    const onSubmit = async (data) => {
        setDisabled(true);
        setReceipt('');
        setError('');
        try {
            const response = await submitLegal('/api/legal/cancel', data);
            setReceipt(response.receiptText || `Folder2Zip – Kündigung eingegangen\nEingang: ${response.receivedAt}\nVertragsende: ${response.terminationAt}`);
        } catch (e) {
            setError(`Übermittlung fehlgeschlagen: ${e.message}`);
        } finally {
            setDisabled(false);
        }
    };

    return (
        <LegalPage browserTitle='Kündigung – Folder2Zip' title='Verträge hier kündigen'>
            <p className='mt-4 text-jus' style={{ textAlign: 'justify' }}>Hier kannst du ein online abgeschlossenes Folder2Zip-Plus-Abonnement ordentlich oder außerordentlich kündigen. Für eine ordentliche Kündigung zum frühestmöglichen Zeitpunkt wird ein eindeutig zuordenbares aktives Stripe-Abo automatisch zum Ende der laufenden bezahlten Periode vorgemerkt.</p>

            <Card className='mt-5' style={cardStyle}>
                <Card.Body style={cardTextStyle}>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className='row formRow mt-3'>
                            <div className='col-12 col-md-6'>
                                <label className='form-label'>Name</label>
                                <input className='form-control formInput' {...register('name', { required: 'Bitte gib deinen Namen ein.', maxLength: 200 })} />
                                {errors.name && <span className='errorMessage'>{errors.name.message}</span>}
                            </div>
                            <div className='col-12 col-md-6 mt-3 mt-md-0'>
                                <label className='form-label'>E-Mail des Vertrags / für die Kündigungsbestätigung</label>
                                <input type='email' className='form-control formInput' {...register('email', { required: 'Bitte gib deine E-Mail-Adresse ein.' })} />
                                {errors.email && <span className='errorMessage'>{errors.email.message}</span>}
                            </div>
                        </div>

                        <div className='row formRow mt-3'>
                            <div className='col-12 col-md-6'>
                                <label className='form-label'>Vertrag / Tarif</label>
                                <select className='form-control formInput' {...register('plan')}>
                                    <option value='monthly'>Folder2Zip Plus – monatlich</option>
                                    <option value='yearly'>Folder2Zip Plus – 12 Monate → monatlich</option>
                                    <option value='any'>Folder2Zip Plus – Tarif unbekannt</option>
                                </select>
                            </div>
                            <div className='col-12 col-md-6 mt-3 mt-md-0'>
                                <label className='form-label'>Eindeutige Vertragsbezeichnung</label>
                                <input className='form-control formInput' placeholder='z. B. Folder2Zip Plus, Stripe-Abo-ID oder Belegnummer' {...register('contractReference', { required: 'Bitte gib eine Vertragsbezeichnung ein.', maxLength: 300 })} />
                                {errors.contractReference && <span className='errorMessage'>{errors.contractReference.message}</span>}
                            </div>
                        </div>

                        <div className='row formRow mt-3'>
                            <div className='col-12 col-md-6'>
                                <label className='form-label'>Art der Kündigung</label>
                                <select className='form-control formInput' {...register('cancellationType')}>
                                    <option value='ordinary'>Ordentliche Kündigung</option>
                                    <option value='extraordinary'>Außerordentliche Kündigung</option>
                                </select>
                            </div>
                            <div className='col-12 col-md-6 mt-3 mt-md-0'>
                                <label className='form-label'>Beendigungszeitpunkt</label>
                                <select className='form-control formInput' {...register('desiredEnd')}>
                                    <option value='earliest'>Zum frühestmöglichen Zeitpunkt</option>
                                    <option value='specific'>Zu einem bestimmten Zeitpunkt</option>
                                </select>
                            </div>
                        </div>

                        {desiredEnd === 'specific' && (
                            <div className='row formRow mt-3'>
                                <div className='col-12'>
                                    <label className='form-label'>Gewünschtes Vertragsende</label>
                                    <input type='date' className='form-control formInput' {...register('desiredDate', { required: 'Bitte wähle das gewünschte Vertragsende.' })} />
                                    {errors.desiredDate && <span className='errorMessage'>{errors.desiredDate.message}</span>}
                                </div>
                            </div>
                        )}

                        {cancellationType === 'extraordinary' && (
                            <div className='row formRow mt-3'>
                                <div className='col-12'>
                                    <label className='form-label'>Grund der außerordentlichen Kündigung</label>
                                    <textarea rows={3} className='form-control formInput' {...register('reason', { required: 'Bitte gib den Grund der außerordentlichen Kündigung an.', maxLength: 2000 })}></textarea>
                                    {errors.reason && <span className='errorMessage'>{errors.reason.message}</span>}
                                </div>
                            </div>
                        )}

                        <button className='btn mt-3' type='submit' disabled={disabled} style={{ background: Props.colorTheme.activeColor, color: 'white' }}>jetzt kündigen</button>
                    </form>
                    {error && <p className='mt-3' style={cardTextStyle}>{error}</p>}
                    <Receipt text={receipt} filename='folder2zip-kuendigung.txt' buttonText='Kündigungsbeleg herunterladen' colorTheme={Props.colorTheme} textStyle={cardTextStyle} />
                </Card.Body>
            </Card>
        </LegalPage>
    );
};

const Cancel = connect((state) => ({
    isDarkTheme: state.theme.isDarkTheme,
    theme: getTheme(state.theme.isDarkTheme),
    colorTheme: getColorThemeByName(state.theme.nameColorTheme),
}))(CancelComponent);

export { Terms, Privacy, Imprint, Withdraw, Cancel };
